import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { DoubleChevLeftIcon } from "@/assets/icons";
import { ProductSwitcherWrapper } from "@/components/sidebar/SidebarFooter/ProductSwitcher";
import { Divider } from "@/components/ui/Divider";
import Tooltip from "@/components/ui/tooltip";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useIsModuleMenuVisible } from "@/hooks/permission/useIsModuleInPlan";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import type { SidebarFooterProps } from "../types";
import ChangePasswordPanel from "./ChangePasswordPanel";
import MyProfilePanel from "./MyProfilePanel";
import { OnboardingMenu } from "./OnboardingMenu";
import { SettingMenuWrapper } from "./SettingMenu";
import { UserMenuWrapper } from "./UserMenu";

const SidebarFooter = ({
  isExpanded,
  toggleSidebar,
  activePopover,
  setActivePopover
}: SidebarFooterProps) => {
  const settingRef = useRef<HTMLButtonElement>(null);
  const userRef = useRef<HTMLButtonElement>(null);
  const onboardingRef = useRef<HTMLButtonElement>(null);
  const productRef = useRef<HTMLButtonElement>(null);
  const { sidebarMenu } = useSelector((state: RootState) => state.ui);
  const { isOnboardingModuleEnabled, isProductSwitcherEnabled } =
    useAppFeatures();

  // A click counts as "outside" only when it lands outside every rendered
  // footer trigger. Triggers that aren't mounted (e.g. Settings without
  // permission) are skipped so they don't wrongly keep a popover open.
  const isPopoverTriggerOrigin = useCallback(
    (e: Event) =>
      [settingRef, userRef, onboardingRef, productRef].every(
        (triggerRef) =>
          !triggerRef.current || !triggerRef.current.contains(e.target as Node)
      ),
    [settingRef, userRef, onboardingRef, productRef]
  );
  const [showMyProfilePanel, setShowMyProfilePanel] = useState(false);
  const [showChangePasswordPanel, setShowChangePasswordPanel] = useState(false);
  const userPermissions = useGetUserPermission();
  const { isVisible: isUserManagementGranted } = useIsModuleMenuVisible(
    EModuleName.USER_MANAGEMENT
  );
  const isUserGroupManagementGranted =
    userPermissions[EModuleName.USER_GROUP_MANAGEMENT][EModulePermission.VIEW];

  return (
    <div
      className="flex flex-col justify-center items-center gap-3 pb-4"
      data-tourid="sidebar-footer"
    >
      {/* The Product Switcher has no backdrop in its design; every other
          footer popover dims the map behind it. */}
      {activePopover !== "" && activePopover !== "productSwitcher" && (
        <div
          className={twMerge(
            "fixed inset-0 bg-black/40 z-[1003]",
            sidebarMenu ? "left-[264px]" : "left-[60px]"
          )}
        />
      )}

      {/* Product Switcher (LI ↔ Targetin) — FF-gated */}
      {isProductSwitcherEnabled && (
        <ProductSwitcherWrapper
          activePopover={activePopover}
          isPopoverTriggerOrigin={isPopoverTriggerOrigin}
          setActivePopover={setActivePopover}
          sidebarMenu={sidebarMenu}
          ref={productRef}
        />
      )}

      {/* Product Overview onboarding panel — gated behind the onboarding-module
          feature flag, rendered above Settings */}
      {isOnboardingModuleEnabled && (
        <OnboardingMenu
          activePopover={activePopover}
          isPopoverTriggerOrigin={isPopoverTriggerOrigin}
          setActivePopover={setActivePopover}
          sidebarMenu={sidebarMenu}
          ref={onboardingRef}
        />
      )}

      {/* Render the setting menu if user management or group management permissions are granted */}
      {(isUserManagementGranted || isUserGroupManagementGranted) && (
        <SettingMenuWrapper
          activePopover={activePopover}
          isPopoverTriggerOrigin={isPopoverTriggerOrigin}
          setActivePopover={setActivePopover}
          sidebarMenu={sidebarMenu}
          ref={settingRef}
        />
      )}
      <Tooltip
        disable={sidebarMenu}
        content="Expand"
        position="right"
        key="Expand"
      >
        <Button
          variant="secondary"
          data-testid="sidebar-toggle"
          className={twMerge(
            "btn-default !border-none hover:bg-teal-50",
            !isExpanded && "rotate-180",
            sidebarMenu && "w-[231px] justify-start"
          )}
          aria-label="Toggle Sidebar"
          onClick={toggleSidebar}
        >
          <DoubleChevLeftIcon />
          <span
            className={twMerge(
              "whitespace-pre text-sm font-medium",
              !sidebarMenu && "opacity-0 translate-x-28 overflow-hidden hidden"
            )}
          >
            Collapse
          </span>
        </Button>
      </Tooltip>
      <Divider className="bg-white-smoke w-full h-[4px]" />
      <UserMenuWrapper
        activePopover={activePopover}
        isPopoverTriggerOrigin={isPopoverTriggerOrigin}
        setActivePopover={setActivePopover}
        sidebarMenu={sidebarMenu}
        setShowMyProfilePanel={setShowMyProfilePanel}
        setShowChangePasswordPanel={setShowChangePasswordPanel}
        ref={userRef}
      />
      <MyProfilePanel
        showMyProfilePanel={showMyProfilePanel}
        setShowMyProfilePanel={setShowMyProfilePanel}
      />
      <ChangePasswordPanel
        showChangePasswordPanel={showChangePasswordPanel}
        setShowChangePasswordPanel={setShowChangePasswordPanel}
      />
    </div>
  );
};

export default SidebarFooter;
